import { useState } from "react";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Lightning, PaperPlaneTilt } from "@phosphor-icons/react";

export default function AutoEscalationToggle({ caseData, onUpdated }) {
    const [busy, setBusy] = useState(false);
    const [dryRunOpen, setDryRunOpen] = useState(false);
    const [dryRunReport, setDryRunReport] = useState(null);
    const [running, setRunning] = useState(false);

    if (!caseData) return null;
    const enabled = !!caseData.auto_escalation_enabled;

    const toggle = async (checked) => {
        setBusy(true);
        try {
            await api.patch(`/cases/${caseData.id}`, { auto_escalation_enabled: checked });
            toast.success(checked ? "Auto-escalation enabled" : "Auto-escalation disabled");
            if (onUpdated) onUpdated();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const runNow = async () => {
        setRunning(true);
        try {
            const { data } = await api.post(`/cases/${caseData.id}/auto-escalate/run`);
            setDryRunReport(data);
            setDryRunOpen(true);
            if (data.succeeded > 0) toast.success(`Escalated ${data.succeeded} offer(s)`);
            else if (data.ripe_count === 0) toast.info("No offers are ripe for escalation yet");
            else toast.warning("Escalation attempts completed with failures");
            if (onUpdated) onUpdated();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRunning(false);
        }
    };

    return (
        <>
            <div
                data-testid="auto-escalation-card"
                className={`flex items-start gap-3 p-4 border rounded-md ${
                    enabled ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"
                }`}
            >
                <Lightning
                    size={22}
                    weight={enabled ? "fill" : "regular"}
                    className={enabled ? "text-emerald-600 shrink-0 mt-0.5" : "text-slate-400 shrink-0 mt-0.5"}
                />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-medium ${enabled ? "text-emerald-900" : "text-slate-900"}`}>
                            Auto-escalate expired offers
                        </p>
                        {enabled && (
                            <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 gap-1">
                                <Lightning size={11} weight="fill" /> ON
                            </Badge>
                        )}
                    </div>
                    <p className={`text-xs mt-0.5 ${enabled ? "text-emerald-800" : "text-slate-600"}`}>
                        If a Pay-for-Deletion offer stays unanswered <strong>3 days past its deadline</strong>, LienLift will
                        auto-generate a validation letter and mail it via <strong>USPS Certified + Return Receipt</strong>.
                        You&apos;ll get an email when it fires.
                    </p>
                    {enabled && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={runNow}
                                disabled={running}
                                data-testid="auto-escalation-run-now"
                                className="h-7 text-xs"
                            >
                                <PaperPlaneTilt size={12} className="mr-1.5" />
                                {running ? "Running…" : "Run now"}
                            </Button>
                            <span className="text-[11px] text-emerald-700">
                                Or wait — scheduled daily at 13:00 UTC (8am ET)
                            </span>
                        </div>
                    )}
                </div>
                <Switch
                    checked={enabled}
                    onCheckedChange={toggle}
                    disabled={busy}
                    data-testid="auto-escalation-switch"
                    aria-label="Toggle auto-escalation"
                />
            </div>

            <Dialog open={dryRunOpen} onOpenChange={setDryRunOpen}>
                <DialogContent className="sm:max-w-lg" data-testid="auto-escalation-report">
                    <DialogHeader>
                        <DialogTitle>Auto-escalation report</DialogTitle>
                        <DialogDescription>
                            Results for this case&apos;s expired offers.
                        </DialogDescription>
                    </DialogHeader>
                    {dryRunReport && (
                        <div className="space-y-3 text-sm">
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-slate-50 border border-slate-200 rounded p-3">
                                    <p className="text-xs text-slate-500 uppercase tracking-wider">Ripe</p>
                                    <p className="text-2xl font-mono text-[#0F1E3D]">{dryRunReport.ripe_count}</p>
                                </div>
                                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                                    <p className="text-xs text-emerald-700 uppercase tracking-wider">Escalated</p>
                                    <p className="text-2xl font-mono text-emerald-800">{dryRunReport.succeeded}</p>
                                </div>
                                <div className="bg-rose-50 border border-rose-200 rounded p-3">
                                    <p className="text-xs text-rose-700 uppercase tracking-wider">Skipped/Failed</p>
                                    <p className="text-2xl font-mono text-rose-800">{dryRunReport.failed_or_skipped}</p>
                                </div>
                            </div>
                            {(dryRunReport.reports || []).map((r, i) => (
                                <div key={i} className={`text-xs p-2.5 rounded border ${
                                    r.ok
                                        ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                                        : "bg-amber-50 border-amber-200 text-amber-900"
                                }`}>
                                    <p className="font-mono">{(r.offer_token || "").slice(0, 12)}…</p>
                                    <p>{r.ok ? "✓ Escalated" : `✕ ${r.reason}`}</p>
                                    {r.letter_id && <p className="text-slate-600">Letter: <span className="font-mono">{r.letter_id.slice(0,8)}…</span></p>}
                                    {r.mailing_id && <p className="text-slate-600">Mailing: <span className="font-mono">{r.mailing_id.slice(0,8)}…</span></p>}
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter>
                        <Button onClick={() => setDryRunOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
